import { useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ModuleRoute } from "./components/ModuleRoute";
import { Sidebar } from "./components/Sidebar";
import { Dashboard } from "./pages/Dashboard";
import { PendingJobs } from "./pages/PendingJobs";
import { Customers } from "./pages/Customers";
import { CustomerDetails } from "./pages/CustomerDetails";
import { Interactions } from "./pages/Interactions";
import { CustomerInfo } from "./pages/CustomerInfo";
import { Assets } from "./pages/Assets";
import { ClientBillingProfile } from "./pages/ClientBillingProfile";
import { Teamviewer } from "./pages/Teamviewer";
import Employee from "./pages/Employee";
import Projects from "./pages/Projects";
import ProjectPortal from "./pages/ProjectPortal";
import { Users } from "./pages/Users";
import { Reports } from "./pages/Reports";
import { KnowledgeBase } from "./pages/KnowledgeBase";
import SupportRequest from "./pages/SupportRequest";
import SupportTickets from "./pages/SupportTickets";
import { TonerManagement } from "./pages/TonerManagement";
import { TonerManagementAdmin } from "./pages/TonerManagementAdmin";
import { SMTPSettings } from "./pages/SMTPSettings";
import { Toaster } from "./components/ui/sonner";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes - no authentication required */}
            <Route path="/support" element={<SupportRequest />} />
            <Route path="/toner-management" element={<TonerManagement />} />
            <Route path="/project-portal/:token" element={<ProjectPortal />} />
            
            {/* Protected routes - authentication required */}
            <Route path="*" element={
              <ProtectedRoute>
                <AuthenticatedApp />
              </ProtectedRoute>
            } />
          </Routes>
          <Toaster />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

function AuthenticatedApp() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-sm border-b border-slate-200">
          <div className="flex items-center justify-between px-6 py-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-md text-slate-600 hover:bg-slate-100"
              data-testid="mobile-menu-button"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            <div className="flex items-center space-x-3">
              <img src="/logo.jpg" alt="Onsite Technology Solutions" className="w-10 h-10 rounded-lg object-cover" />
              <h1 className="text-2xl font-semibold text-slate-900">Onsite Technology Solutions CRM</h1>
            </div>
            
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center text-white font-semibold">
              OTS
            </div>
          </div>
        </header>
        
        <main className="flex-1 overflow-auto bg-slate-50 p-6">
          <Routes>
            <Route path="/" element={<ModuleRoute module="pending_jobs"><PendingJobs /></ModuleRoute>} />
            <Route path="/dashboard" element={<ModuleRoute module="dashboard"><Dashboard /></ModuleRoute>} />
            <Route path="/pending-jobs" element={<ModuleRoute module="pending_jobs"><PendingJobs /></ModuleRoute>} />
            <Route path="/customers" element={<ModuleRoute module="customers"><Customers /></ModuleRoute>} />
            <Route path="/customers/:id" element={<ModuleRoute module="customers"><CustomerDetails /></ModuleRoute>} />
            <Route path="/customer-info" element={<ModuleRoute module="customer_info"><CustomerInfo /></ModuleRoute>} />
            <Route path="/interactions" element={<ModuleRoute module="interactions"><Interactions /></ModuleRoute>} />
            <Route path="/assets" element={<ModuleRoute module="assets"><Assets /></ModuleRoute>} />
            <Route path="/client-billing" element={<ModuleRoute module="client_billing"><ClientBillingProfile /></ModuleRoute>} />
            <Route path="/teamviewer" element={<ModuleRoute module="teamviewer"><Teamviewer /></ModuleRoute>} />
            <Route path="/employee" element={<ModuleRoute module="employee"><Employee /></ModuleRoute>} />
            <Route path="/projects" element={<ModuleRoute module="projects"><Projects /></ModuleRoute>} />
            <Route path="/reports" element={<ModuleRoute module="reports"><Reports /></ModuleRoute>} />
            <Route path="/knowledge-base" element={<ModuleRoute module="knowledge_base"><KnowledgeBase /></ModuleRoute>} />
            <Route path="/users" element={<ModuleRoute module="users"><Users /></ModuleRoute>} />
            <Route path="/support-tickets" element={<ModuleRoute module="support_tickets"><SupportTickets /></ModuleRoute>} />
            <Route path="/toner-admin" element={<ModuleRoute module="toner_management"><TonerManagementAdmin /></ModuleRoute>} />
            <Route path="/smtp-settings" element={<ModuleRoute module="smtp_settings"><SMTPSettings /></ModuleRoute>} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default App;
